import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { machinesApi, Machine, MaintenanceRecord, AddMaintenancePayload, CreateMachinePayload } from '../../api/machines';
import { useAuthStore } from '../../store/authStore';
import {
  Plus, X, Wrench, AlertTriangle, CheckCircle, Clock,
  ChevronRight, Settings, History, RefreshCw,
} from 'lucide-react';

// ─── Design tokens ─────────────────────────────────────────────────────────────

const C = {
  bg: '#0a0a0f',
  card: '#12121a',
  border: '#1e1e2e',
  borderHover: '#2e2d3d',
  textPrimary: '#e2e1f0',
  textSecondary: '#6b6880',
  textMuted: '#4a4858',
  orange: '#f97316',
  orangeHover: '#ea580c',
  success: '#22c55e',
  error: '#ef4444',
  warning: '#f59e0b',
  blue: '#3b82f6',
};

// ─── Status config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  available: { label: 'Disponible', color: C.success, dot: '#22c55e' },
  in_use:    { label: 'En uso',     color: C.warning,  dot: '#f59e0b' },
  maintenance: { label: 'Mantenimiento', color: C.error, dot: '#ef4444' },
};

const TYPE_LABELS: Record<string, string> = {
  impresora: 'Impresora',
  dtf:       'DTF',
  ploter:    'Plóter',
  laminadora:'Laminadora',
  cortadora: 'Cortadora',
  otra:      'Otra',
};

const DEPT_LABELS: Record<string, string> = {
  imprenta: 'Imprenta',
  dtf:      'DTF',
};

const MAINTENANCE_TYPE_LABELS: Record<string, string> = {
  preventivo: 'Preventivo',
  correctivo: 'Correctivo',
  revision:   'Revisión',
};

const MAINTENANCE_TYPE_COLORS: Record<string, string> = {
  preventivo: C.success,
  correctivo: C.error,
  revision:   C.blue,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0a0a0f', border: `1px solid ${C.border}`,
  borderRadius: 8, color: C.textPrimary, fontSize: 13, padding: '10px 12px',
  fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: C.textMuted, letterSpacing: '0.06em',
  fontFamily: 'JetBrains Mono, monospace', display: 'block', marginBottom: 6,
  textTransform: 'uppercase',
};

function formatDate(s: string) {
  return new Date(s).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatCurrency(n?: number | null) {
  if (n == null) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n);
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.available;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 10px', borderRadius: 20,
      background: `${cfg.color}15`, border: `1px solid ${cfg.color}40`,
      color: cfg.color, fontSize: 11, fontWeight: 600,
      fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
}

// ─── Machine card ─────────────────────────────────────────────────────────────

function MachineCard({ machine, selected, onClick }: { machine: Machine; selected: boolean; onClick: () => void }) {
  const cfg = STATUS_CONFIG[machine.status] || STATUS_CONFIG.available;
  return (
    <div
      onClick={onClick}
      style={{
        background: selected ? '#1a1929' : C.card,
        border: `1px solid ${selected ? C.orange : C.border}`,
        borderRadius: 12, padding: '16px 18px',
        cursor: 'pointer', transition: 'all 0.15s',
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* status stripe */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: cfg.color, opacity: 0.8, borderRadius: '12px 12px 0 0',
      }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 4, gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, fontFamily: 'Syne, sans-serif', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {machine.name}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: C.textSecondary, background: '#1e1e2e', padding: '2px 8px', borderRadius: 6 }}>
              {TYPE_LABELS[machine.type] || machine.type}
            </span>
            <span style={{ fontSize: 11, color: C.orange, background: 'rgba(249,115,22,0.1)', padding: '2px 8px', borderRadius: 6, border: '1px solid rgba(249,115,22,0.2)' }}>
              {DEPT_LABELS[machine.department] || machine.department}
            </span>
          </div>
        </div>
        <ChevronRight size={14} color={selected ? C.orange : C.textMuted} style={{ flexShrink: 0, marginTop: 2 }} />
      </div>
      <div style={{ marginTop: 12 }}>
        <StatusBadge status={machine.status} />
      </div>
    </div>
  );
}

// ─── Maintenance modal ────────────────────────────────────────────────────────

function MaintenanceModal({ machine, onClose, onSaved }: {
  machine: Machine;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<AddMaintenancePayload>({
    type: 'revision',
    description: '',
    performedBy: '',
    performedAt: new Date().toISOString().slice(0, 16),
    cost: null,
    nextMaintenanceDate: null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!form.description.trim()) { setError('La descripción es obligatoria'); return; }
    if (!form.performedBy.trim()) { setError('El técnico es obligatorio'); return; }
    setSaving(true);
    setError(null);
    try {
      await machinesApi.addMaintenance(machine.id, {
        ...form,
        performedAt: form.performedAt ? new Date(form.performedAt).toISOString() : undefined,
        nextMaintenanceDate: form.nextMaintenanceDate || null,
        cost: form.cost !== null && form.cost !== undefined && String(form.cost) !== '' ? Number(form.cost) : null,
      });
      qc.invalidateQueries({ queryKey: ['machines'] });
      qc.invalidateQueries({ queryKey: ['machine', machine.id] });
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, backdropFilter: 'blur(4px)' }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        zIndex: 301, width: '90%', maxWidth: 520, background: C.card,
        border: `1px solid ${C.border}`, borderRadius: 16,
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflowY: 'auto', maxHeight: '90vh',
      }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Wrench size={16} color={C.orange} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 17, color: '#fff', margin: 0, letterSpacing: '-0.02em' }}>
              Registrar Mantenimiento
            </h2>
            <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 1 }}>{machine.name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: 4 }}><X size={18} /></button>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && (
            <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: C.error, display: 'flex', gap: 8, alignItems: 'center' }}>
              <AlertTriangle size={14} /> {error}
            </div>
          )}

          <div>
            <label style={labelStyle}>Tipo de mantenimiento</label>
            <select
              value={form.type}
              onChange={e => setForm(f => ({ ...f, type: e.target.value as any }))}
              style={{ ...inputStyle }}
            >
              <option value="preventivo">Preventivo</option>
              <option value="correctivo">Correctivo</option>
              <option value="revision">Revisión</option>
            </select>
          </div>

          <div>
            <label style={labelStyle}>Fecha de realización</label>
            <input
              type="datetime-local"
              value={form.performedAt || ''}
              onChange={e => setForm(f => ({ ...f, performedAt: e.target.value }))}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Descripción *</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe el trabajo realizado..."
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
            />
          </div>

          <div>
            <label style={labelStyle}>Técnico responsable *</label>
            <input
              value={form.performedBy}
              onChange={e => setForm(f => ({ ...f, performedBy: e.target.value }))}
              placeholder="Nombre del técnico"
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Coste (€)</label>
              <input
                type="number"
                step="0.01"
                value={form.cost ?? ''}
                onChange={e => setForm(f => ({ ...f, cost: e.target.value ? Number(e.target.value) : null }))}
                placeholder="0.00"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Próximo mantenimiento</label>
              <input
                type="date"
                value={form.nextMaintenanceDate || ''}
                onChange={e => setForm(f => ({ ...f, nextMaintenanceDate: e.target.value || null }))}
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            onClick={onClose}
            style={{ padding: '9px 18px', borderRadius: 8, background: 'transparent', border: `1px solid ${C.border}`, color: C.textSecondary, fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ padding: '9px 18px', borderRadius: 8, background: saving ? '#7a3a0a' : C.orange, border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer', fontFamily: 'DM Sans, sans-serif', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {saving ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Guardando...</> : <><Wrench size={13} /> Registrar</>}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── New Machine modal ────────────────────────────────────────────────────────

function NewMachineModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CreateMachinePayload>({
    name: '',
    type: 'impresora',
    department: 'imprenta',
    status: 'available',
    description: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!form.name.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true);
    setError(null);
    try {
      await machinesApi.createMachine({ ...form, description: form.description || null });
      qc.invalidateQueries({ queryKey: ['machines'] });
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Error al crear la máquina');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, backdropFilter: 'blur(4px)' }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        zIndex: 301, width: '90%', maxWidth: 480, background: C.card,
        border: `1px solid ${C.border}`, borderRadius: 16,
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
      }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Plus size={16} color={C.orange} />
          </div>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 17, color: '#fff', margin: 0, flex: 1, letterSpacing: '-0.02em' }}>Nueva Máquina</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: 4 }}><X size={18} /></button>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && (
            <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: C.error, display: 'flex', gap: 8, alignItems: 'center' }}>
              <AlertTriangle size={14} /> {error}
            </div>
          )}

          <div>
            <label style={labelStyle}>Nombre *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Impresora HP DesignJet" style={inputStyle} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Tipo</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={inputStyle}>
                <option value="impresora">Impresora</option>
                <option value="dtf">DTF</option>
                <option value="ploter">Plóter</option>
                <option value="laminadora">Laminadora</option>
                <option value="cortadora">Cortadora</option>
                <option value="otra">Otra</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Departamento</label>
              <select value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} style={inputStyle}>
                <option value="imprenta">Imprenta</option>
                <option value="dtf">DTF</option>
              </select>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Descripción</label>
            <textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Descripción opcional..." rows={2} style={{ ...inputStyle, resize: 'none' }} />
          </div>
        </div>

        <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, background: 'transparent', border: `1px solid ${C.border}`, color: C.textSecondary, fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '9px 18px', borderRadius: 8, background: saving ? '#7a3a0a' : C.orange, border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer', fontFamily: 'DM Sans, sans-serif', display: 'flex', alignItems: 'center', gap: 6 }}>
            {saving ? 'Guardando...' : <><Plus size={13} /> Crear Máquina</>}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Side panel ───────────────────────────────────────────────────────────────

function MachinePanel({ machine, onClose, isAdmin }: { machine: Machine; onClose: () => void; isAdmin: boolean }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'info' | 'maintenance'>('info');
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);

  const { data: machineDetail, isLoading } = useQuery({
    queryKey: ['machine', machine.id],
    queryFn: () => machinesApi.getMachine(machine.id),
    staleTime: 30_000,
  });

  const detail = machineDetail?.data ?? machine;
  const history: MaintenanceRecord[] = detail.maintenanceHistory ?? [];

  const handleChangeStatus = async (status: string) => {
    setChangingStatus(true);
    try {
      await machinesApi.updateMachineStatus(machine.id, status);
      qc.invalidateQueries({ queryKey: ['machines'] });
      qc.invalidateQueries({ queryKey: ['machine', machine.id] });
    } catch {
      // silently ignore
    } finally {
      setChangingStatus(false);
    }
  };

  return (
    <div style={{
      width: 380, flexShrink: 0, background: '#0e0e18',
      borderLeft: `1px solid ${C.border}`,
      display: 'flex', flexDirection: 'column',
      height: '100%', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ padding: '18px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 16, color: '#fff', margin: 0, letterSpacing: '-0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {detail.name}
          </h3>
          <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 3 }}>
            {TYPE_LABELS[detail.type] || detail.type} · {DEPT_LABELS[detail.department] || detail.department}
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: 2, flexShrink: 0 }}>
          <X size={16} />
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, padding: '0 20px' }}>
        {(['info', 'maintenance'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '12px 14px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 12, fontWeight: 600, fontFamily: 'DM Sans, sans-serif',
            color: tab === t ? C.orange : C.textSecondary,
            borderBottom: tab === t ? `2px solid ${C.orange}` : '2px solid transparent',
            marginBottom: -1, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {t === 'info' ? <><Settings size={12} /> Info</> : <><History size={12} /> Mantenimientos</>}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        {tab === 'info' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <StatusBadge status={detail.status} />
            </div>

            {detail.description && (
              <div>
                <div style={{ fontSize: 11, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', marginBottom: 4, textTransform: 'uppercase' }}>Descripción</div>
                <p style={{ fontSize: 13, color: C.textSecondary, margin: 0, lineHeight: 1.6 }}>{detail.description}</p>
              </div>
            )}

            <div>
              <div style={{ fontSize: 11, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', marginBottom: 4, textTransform: 'uppercase' }}>Registrada</div>
              <div style={{ fontSize: 13, color: C.textSecondary }}>{formatDate(detail.createdAt)}</div>
            </div>

            {isAdmin && (
              <div>
                <div style={{ fontSize: 11, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', marginBottom: 8, textTransform: 'uppercase' }}>Cambiar Estado</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(['available', 'in_use', 'maintenance'] as const).map(s => {
                    const cfg = STATUS_CONFIG[s];
                    const isActive = detail.status === s;
                    return (
                      <button
                        key={s}
                        disabled={isActive || changingStatus}
                        onClick={() => handleChangeStatus(s)}
                        style={{
                          padding: '8px 14px', borderRadius: 8, cursor: isActive ? 'default' : 'pointer',
                          background: isActive ? `${cfg.color}18` : 'transparent',
                          border: `1px solid ${isActive ? cfg.color + '50' : C.border}`,
                          color: isActive ? cfg.color : C.textSecondary,
                          fontSize: 13, fontFamily: 'DM Sans, sans-serif', fontWeight: isActive ? 600 : 400,
                          display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.15s',
                          opacity: changingStatus && !isActive ? 0.5 : 1,
                        }}
                      >
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
                        {cfg.label}
                        {isActive && <CheckCircle size={12} style={{ marginLeft: 'auto' }} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'maintenance' && (
          <div>
            {isLoading && (
              <div style={{ textAlign: 'center', padding: '24px 0', color: C.textMuted, fontSize: 13 }}>Cargando...</div>
            )}
            {!isLoading && history.length === 0 && (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <Wrench size={28} color={C.textMuted} style={{ marginBottom: 10 }} />
                <div style={{ fontSize: 13, color: C.textMuted }}>Sin registros de mantenimiento</div>
              </div>
            )}
            {history.map(rec => (
              <div key={rec.id} style={{ marginBottom: 12, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                    background: `${MAINTENANCE_TYPE_COLORS[rec.type] || C.blue}18`,
                    border: `1px solid ${MAINTENANCE_TYPE_COLORS[rec.type] || C.blue}40`,
                    color: MAINTENANCE_TYPE_COLORS[rec.type] || C.blue,
                  }}>
                    {MAINTENANCE_TYPE_LABELS[rec.type] || rec.type}
                  </span>
                  <span style={{ fontSize: 11, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatDate(rec.performedAt)}
                  </span>
                </div>
                <p style={{ fontSize: 13, color: C.textSecondary, margin: '6px 0', lineHeight: 1.5 }}>{rec.description}</p>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6 }}>
                  <div style={{ fontSize: 11, color: C.textMuted }}>
                    <span style={{ color: C.textSecondary }}>Técnico:</span> {rec.performedBy}
                  </div>
                  {rec.cost != null && (
                    <div style={{ fontSize: 11, color: C.textMuted }}>
                      <span style={{ color: C.textSecondary }}>Coste:</span> {formatCurrency(rec.cost)}
                    </div>
                  )}
                  {rec.nextMaintenanceDate && (
                    <div style={{ fontSize: 11, color: C.warning, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Clock size={10} /> Próximo: {formatDate(rec.nextMaintenanceDate)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer actions */}
      {isAdmin && (
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.border}` }}>
          <button
            onClick={() => setShowMaintenanceModal(true)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 9,
              background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)',
              color: C.orange, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <Wrench size={14} /> Registrar Mantenimiento
          </button>
        </div>
      )}

      {showMaintenanceModal && (
        <MaintenanceModal
          machine={detail}
          onClose={() => setShowMaintenanceModal(false)}
          onSaved={() => {
            setShowMaintenanceModal(false);
            setTab('maintenance');
          }}
        />
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function getUserDepartmentLabel(hasRole: (r: string) => boolean): string | null {
  if (hasRole('ROLE_IMPRENTA')) return 'Imprenta';
  if (hasRole('ROLE_DTF')) return 'DTF';
  return null;
}

export default function MaquinasPage() {
  const { hasRole } = useAuthStore();
  const isAdmin = hasRole('ROLE_ADMIN');
  const canWrite = isAdmin; // POST/PATCH require ROLE_ADMIN (ERP-70)
  const canFilterDept = isAdmin || hasRole('ROLE_GESTOR');
  const userDeptLabel = canFilterDept ? null : getUserDepartmentLabel(hasRole);

  const [filterDept, setFilterDept] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);

  const params: Record<string, string> = {};
  if (filterDept) params.department = filterDept;
  if (filterStatus) params.status = filterStatus;

  const { data, isLoading } = useQuery({
    queryKey: ['machines', filterDept, filterStatus],
    queryFn: () => machinesApi.getMachines(params),
    staleTime: 30_000,
  });

  const machines = data?.data ?? [];

  const statusCounts = machines.reduce((acc, m) => {
    acc[m.status] = (acc[m.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div style={{ display: 'flex', height: '100%', background: C.bg, minHeight: 0 }}>
      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {/* Header */}
        <div style={{ padding: '24px 28px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 22, color: '#fff', margin: 0, letterSpacing: '-0.03em' }}>
                  Máquinas
                </h1>
                {userDeptLabel && (
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                    background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)',
                    color: C.orange, fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
                  }}>
                    {userDeptLabel}
                  </span>
                )}
              </div>
              <p style={{ fontSize: 13, color: C.textSecondary, margin: 0 }}>
                {userDeptLabel
                  ? `Máquinas de tu departamento · ${userDeptLabel}`
                  : 'Gestión de maquinaria y mantenimientos'}
              </p>
            </div>
            {canWrite ? (
              <button
                onClick={() => setShowNewModal(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 18px', borderRadius: 10,
                  background: C.orange, border: 'none', color: '#fff',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'DM Sans, sans-serif',
                  boxShadow: '0 4px 16px rgba(249,115,22,0.3)',
                }}
              >
                <Plus size={15} /> Nueva Máquina
              </button>
            ) : (
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 20,
                background: 'rgba(107,104,128,0.12)', border: '1px solid rgba(107,104,128,0.3)',
                color: C.textSecondary, fontFamily: 'DM Sans, sans-serif',
              }}>
                Solo lectura
              </span>
            )}
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <div key={key} style={{
                padding: '8px 16px', borderRadius: 10,
                background: `${cfg.color}10`, border: `1px solid ${cfg.color}30`,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.dot }} />
                <span style={{ fontSize: 12, color: cfg.color, fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>
                  {cfg.label}: <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{statusCounts[key] || 0}</span>
                </span>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            {canFilterDept && (
              <select
                value={filterDept}
                onChange={e => setFilterDept(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: 8, background: C.card, border: `1px solid ${C.border}`, color: filterDept ? C.textPrimary : C.textMuted, fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', outline: 'none' }}
              >
                <option value="">Todos los departamentos</option>
                <option value="imprenta">Imprenta</option>
                <option value="dtf">DTF</option>
              </select>
            )}
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 8, background: C.card, border: `1px solid ${C.border}`, color: filterStatus ? C.textPrimary : C.textMuted, fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', outline: 'none' }}
            >
              <option value="">Todos los estados</option>
              <option value="available">Disponible</option>
              <option value="in_use">En uso</option>
              <option value="maintenance">Mantenimiento</option>
            </select>
            {(filterDept || filterStatus) && (
              <button
                onClick={() => { setFilterDept(''); setFilterStatus(''); }}
                style={{ padding: '8px 12px', borderRadius: 8, background: 'transparent', border: `1px solid ${C.border}`, color: C.textSecondary, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <X size={11} /> Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Grid */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 28px 28px' }}>
          {isLoading && (
            <div style={{ textAlign: 'center', padding: '48px 0', color: C.textMuted, fontSize: 13 }}>
              Cargando máquinas...
            </div>
          )}
          {!isLoading && machines.length === 0 && (
            <div style={{ textAlign: 'center', padding: '56px 0' }}>
              <Wrench size={36} color={C.textMuted} style={{ marginBottom: 14 }} />
              <div style={{ fontSize: 15, fontWeight: 600, color: C.textSecondary, marginBottom: 6, fontFamily: 'Syne, sans-serif' }}>
                Sin máquinas
              </div>
              <div style={{ fontSize: 13, color: C.textMuted }}>
                {filterDept || filterStatus ? 'No hay máquinas con los filtros aplicados.' : 'Aún no se han registrado máquinas.'}
              </div>
            </div>
          )}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 12,
          }}>
            {machines.map(m => (
              <MachineCard
                key={m.id}
                machine={m}
                selected={selectedMachine?.id === m.id}
                onClick={() => setSelectedMachine(prev => prev?.id === m.id ? null : m)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Side panel */}
      {selectedMachine && (
        <MachinePanel
          machine={selectedMachine}
          onClose={() => setSelectedMachine(null)}
          isAdmin={isAdmin}
        />
      )}

      {/* New machine modal */}
      {showNewModal && (
        <NewMachineModal
          onClose={() => setShowNewModal(false)}
          onSaved={() => setShowNewModal(false)}
        />
      )}
    </div>
  );
}
